import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import styles from '../assets/guardianPropertyDetails.module.css';

// Import services
import guardianPropertySearchService from '../services/guardianPropertySearchService';
// import keepMeInformedService from '../services/keepMeInformedService';
import keepMeInformedService from '../services/mockDataService'; // Use mock service for testing

const GuardianPropertyDetails = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasNotification, setHasNotification] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);

  useEffect(() => {
    fetchPropertyDetails();
    checkNotificationStatus();
  }, [propertyId]);

  const fetchPropertyDetails = async () => {
    try {
      setLoading(true);
      // Use the existing property search service to get details
      const data = await guardianPropertySearchService.getPropertyDetails(
        propertyId
      );
      setProperty(data);
    } catch (error) {
      console.error('Error fetching property details:', error);
      toast.error('Failed to load property details');
    } finally {
      setLoading(false);
    }
  };

  const checkNotificationStatus = async () => {
    try {
      // Check if user already has a notification for this property
      const notifications = await keepMeInformedService.getNotifications();
      console.log('Notifications:', notifications);
      const hasNotificationForProperty = notifications.data.some(
        (notification) => notification.property_id === parseInt(propertyId)
      );
      console.log(
        'Has notification for property:',
        hasNotificationForProperty,
        'Property ID:',
        propertyId
      );
      setHasNotification(hasNotificationForProperty);
    } catch (error) {
      console.error('Error checking notification status:', error);
      // Don't show error toast for this, as it's not critical
    }
  };

  const handleViewNotifications = () => {
    // Navigate to the Keep Me Informed page
    navigate('/keep-me-informed');
  };

  const handleKeepMeInformed = async () => {
    try {
      setNotificationLoading(true);
      console.log('Adding keep me informed for property:', propertyId);
      await keepMeInformedService.addKeepMeInformed(propertyId);
      toast.success('You will be informed about new viewings!');
      setHasNotification(true);
      console.log('Successfully added keep me informed notification');
    } catch (error) {
      console.error('Error adding keep me informed:', error);
      toast.error('Failed to add notification. Please try again.');
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleBackToSearch = () => {
    navigate('/guardian-property-search');
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading property details...</div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>Property not found</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.propertyTitle}>
            {property.title || property.address}
          </h1>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.spacesInfo}>
            <span className={styles.spacesIcon}>🛒</span>
            <span className={styles.spacesText}>
              {property.total_spaces || 1}
            </span>
            <span className={styles.spacesIcon}>🍴</span>
            <span className={styles.spacesText}>
              {property.available_spaces || 1}
            </span>
            <span className={styles.spacesLabel}>
              Spaces: {property.total_spaces || 1}
            </span>
            <span className={styles.spacesLabel}>
              Available Spaces: {property.available_spaces || 1}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Left Section - Image/Map */}
        <div className={styles.leftSection}>
          <div className={styles.imageSection}>
            <div className={styles.imagePlaceholder}>
              {/* This would be replaced with actual property images */}
              <div className={styles.imageOverlay}>Property Image</div>
            </div>
          </div>
        </div>

        {/* Right Section - Pricing */}
        <div className={styles.rightSection}>
          <div className={styles.pricingPanel}>
            {hasNotification ? (
              <button
                className={styles.viewNotificationsBtn}
                onClick={handleViewNotifications}
              >
                View Notifications
              </button>
            ) : (
              <>
                <p
                  style={{
                    fontSize: '18px',
                    marginBottom: '15px',
                    color: '#666',
                  }}
                >
                  There is no viewing scheduled at present. Click button to be
                  informed when the next viewing will take place.
                </p>
                <button
                  className={`${styles.viewNotificationsBtn} ${styles.keepMeInformedBtn}`}
                  onClick={handleKeepMeInformed}
                  disabled={notificationLoading}
                >
                  {notificationLoading ? 'Adding...' : 'Keep me Informed'}
                </button>
              </>
            )}

            <div className={styles.pricingContent}>
              <div className={styles.normalPriceSection}>
                <span className={styles.normalPriceLabel}>
                  Normal Area Price
                </span>
                <span className={styles.normalPrice}>
                  £{property.normal_area_price || 900}
                </span>
              </div>

              <div className={styles.youPaySection}>
                <span className={styles.youPayLabel}>You Pay</span>
                <span className={styles.youPayPrice}>
                  £{property.price || 800}
                </span>
              </div>

              <div className={styles.savingsSection}>
                <span className={styles.savingsLabel}>You Save</span>
                <span className={styles.savingsPercentage}>
                  {property.savings_percentage || 11}%
                </span>
              </div>

              {/* Property Details */}
              <div className={styles.propertyDetailsGrid}>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Area:</span>
                  <span className={styles.detailValue}>
                    {property.area || 'South London'}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Total Spaces:</span>
                  <span className={styles.detailValue}>
                    {property.total_spaces || 1}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Available Spaces:</span>
                  <span className={styles.detailValue}>
                    {property.available_spaces || 1}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Bedrooms:</span>
                  <span className={styles.detailValue}>
                    {property.bedrooms || 1}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Kitchens:</span>
                  <span className={styles.detailValue}>
                    {property.kitchens || 1}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Garden:</span>
                  <span className={styles.detailValue}>
                    {property.garden || 'no'}
                  </span>
                </div>
                <div className={styles.detailRow}>
                  <span className={styles.detailLabel}>Parking:</span>
                  <span className={styles.detailValue}>
                    {property.parking || 'no'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Description Section */}
      <div className={styles.descriptionSection}>
        <div className={styles.descriptionContent}>
          <p className={styles.mainDescription}>
            {property.description ||
              'Global Guardians are offering you this amazing unit in Woolwich, South London, for only £800 pcm.'}
          </p>

          <div className={styles.propertyIncludes}>
            <p>
              <strong>The property includes:</strong>
            </p>
            <ul>
              {property.property_includes ? (
                property.property_includes.map((item, index) => (
                  <li key={index}>{item}</li>
                ))
              ) : (
                <>
                  <li>Fitted kitchen</li>
                  <li>Fitted bathroom</li>
                  <li>Generous rooms</li>
                </>
              )}
            </ul>
          </div>

          <p className={styles.locationDescription}>
            {property.location_description ||
              'This is a fantastic opportunity to enjoy living in South London at a much lower cost.'}
          </p>

          <p className={styles.nearbyAmenities}>
            {property.nearby_amenities ||
              'Close by you will find a selection of supermarkets, shops, health centre/gym, post office, restaurants and bars. Lastly, it is only 20 mins walk from the stunning Charlton Park.'}
          </p>

          <p className={styles.transportDescription}>
            {property.transport_description ||
              'This would be a great deal for anyone working in Central London, being situated a 10 mins walk from Woolwich Arsenal Station which can get to London Bridge Station in just 25 mins.'}
          </p>

          <p className={styles.viewingNote}>
            {property.viewing_note ||
              'Request your viewing today or give us a call for more information.'}
          </p>
        </div>
      </div>

      {/* Navigation */}
      <div className={styles.navigation}>
        <button className={styles.backButton} onClick={handleBackToSearch}>
          ← Back to Property Search
        </button>
      </div>
    </div>
  );
};

export default GuardianPropertyDetails;
